import React, { lazy, Suspense } from "react";
import { Skeleton, Typography } from "antd/lib";
import useAxiosQuery from "@/hooks/useMyQuery";
import { useTranslations } from "next-intl";
import PeopleAlsoWatch from "@/app/components/products/PeopleAlsoWatch";
import MyCard from "@/app/components/MyCard";

// Lazy loading the components
const ProductFundRounds = lazy(() => import("./ProductFundRounds"));
const ProductTokenSale = lazy(() => import("./ProductTokenSale"));
const ProductTokenomics = lazy(() => import("./ProductTokenomics"));
const ProductInfo = lazy(() => import("./ProductInfo"));
const ProductFAQs = lazy(() => import("./ProductFAQ"));

export interface IFAQ {
  question_name: string;
  answer: string;
}
interface IPlatform {
  name: string;
  logo_uri: string;
}
export interface ISaleInfo {
  time: string;
  price: number;
  token_for_sale: number;
  total_raise: number;
  platform: IPlatform;
}

export interface ITokenomic {
  total_raised: number;
  initial_value: {
    market_cap: number;
    fdmc: number;
    circulating: number;
  };
  token_allocation: {
    total_supply: number;
    private_sale: number;
    public_sale: number;
  };
  tokenomics: {
    name: string;
    percent: number;
  }[];
}
export interface IFundingRound {
  time: string;
  funding_round: string;
  amount_raised: string;
  investor: IPlatform[];
}
export interface IPriceInformation {
  low_24h: number;
  high_24h: number;
  volume: number;
  no_chains: number;
  ath: {
    timestamp: string;
    price: number;
  };
  atl: {
    timestamp: string;
    price: number;
  };
  market_rank: number;
  price: number;
  market_cap: number;
  market_cap_dominance: number;
  circulating_supply: number;
  max_supply: number;
  total_supply: number;
}
type ProfileFullResponse = {
  about: string;
  funding_round: [];
  token_sale?: {
    sale_info: ISaleInfo[];
    backers: IPlatform[];
  };
  price_information: IPriceInformation;
  tokenomics?: ITokenomic;
  faq?: IFAQ[];
};

const ProductProfilePage = ({ symbol }: { symbol: string }) => {
  const t = useTranslations("products");
  const { data, isLoading } = useAxiosQuery<{ data?: ProfileFullResponse }>({
    url: "v1/product/token-profile-full",
    params: { symbol },
  });
  
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="col-span-2 flex flex-col gap-6">
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : (
            <MyCard
              styles={{ body: { padding: 0 } }}
              title={
                <Typography.Title level={5} className="mb-0">
                  {t("About")} {symbol}
                </Typography.Title>
              }
            >
              <Typography.Paragraph className="max-h-[300px] overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: data?.data?.about || "" }} />
              </Typography.Paragraph>
            </MyCard>
          )}

          <Suspense fallback={<Skeleton active paragraph={{ rows: 4 }} />}>
            {data?.data?.funding_round?.length ? <ProductFundRounds data={data?.data?.funding_round} /> : null}
          </Suspense>

          <Suspense fallback={<Skeleton active paragraph={{ rows: 4 }} />}>
            {data?.data?.token_sale?.sale_info?.length ? (
              <ProductTokenSale data={data?.data?.token_sale?.sale_info || []} />
            ) : null}
          </Suspense>

          <Suspense fallback={<Skeleton active paragraph={{ rows: 4 }} />}>
            {data?.data?.tokenomics?.tokenomics?.length ? (
              <ProductTokenomics data={data?.data?.tokenomics} isLoading={isLoading} />
            ) : null}
          </Suspense>
        </div>

        <div className="lg:col-span-1">
          <Suspense fallback={<Skeleton active paragraph={{ rows: 4 }} />}>
            <ProductInfo symbol={symbol} priceInformation={data?.data?.price_information} />
          </Suspense>
        </div>
      </div>

      <Suspense fallback={<Skeleton active paragraph={{ rows: 4 }} />}>
        <ProductFAQs data={data?.data?.faq || []} />
      </Suspense>

      <Suspense fallback={<Skeleton active paragraph={{ rows: 4 }} />}>
        <PeopleAlsoWatch symbol={symbol} />
      </Suspense>
    </div>
  );
};

export default ProductProfilePage;
